import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./ContactInformation.css";
import StatusIcon from "./StatusIcon";
 
const schema = yup.object().shape({
  firstName: yup
    .string()
    .required("First name cannot be blank")
    .max(100, "First name must be at most 100 characters"),
 
  lastName: yup
    .string()
    .required("Last name cannot be blank")
    .max(100, "Last name must be at most 100 characters"),

    email: yup
  .string()
  .required("Email cannot be blank")
  .max(100, "Email must be at most 100 characters")
  .matches(
    /^(?!.*\.\.)(?!-)[^@\s]+@(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(?<!\.)$/,
    "Enter a valid email address"
  ),
 
  telephone: yup
    .string()
    .max(100, "Telephone must be at most 100 digits")
    .required("Telephone cannot be blank")
    .matches(/^[\d+\s]*$/, "Enter a valid telephone number"),
 
  showSecondaryContact: yup.boolean().default(false),
 
  secondaryFirstName: yup
    .string()
    .max(100, "First name must be at most 100 characters")
    .when("showSecondaryContact", (show, schema) =>
      show ? schema.required("First name cannot be blank") : schema
    ),
 
  secondaryLastName: yup
    .string()
    .max(100, "Last name must be at most 100 characters")
    .when("showSecondaryContact", (show, schema) =>
      show ? schema.required("Last name cannot be blank") : schema
    ),
 
secondaryEmail: yup
  .string()
  .max(100, "Secondary email must be at most 100 characters")
  .when("showSecondaryContact", (show, schema) =>
    show
      ? schema
          .required("Email cannot be blank")
          .matches(
            /^(?!.*\.\.)(?!-)[^@\s]+@(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(?<!\.)$/,
            "Enter a valid email address"
          )
      : schema
  ),
 
  secondaryTelephone: yup
    .string()
    .max(100, "Telephone must be at most 100 digits")
    .when("showSecondaryContact", (show, schema) =>
      show
        ? schema
            .required("Telephone cannot be blank")
            .matches(
              /^[\d+\s]*$/,
              "Enter a valid telephone number"
            )
        : schema
    ),
});
 
const ContactInformation = forwardRef(({ formData, onContactDataChange }, ref) => {
  const [showSecondaryContact, setShowSecondaryContact] = useState(formData?.showSecondaryContact || false);
  const [isInitialized, setIsInitialized] = useState(false);
 
  const {
    register,
    setValue,
    getValues,
    trigger,
    resetField,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      firstName: formData?.firstName || "",
      lastName: formData?.lastName || "",
      email: formData?.email || "",
      telephone: formData?.telephone || "",
      secondaryFirstName: formData?.secondaryFirstName || "",
      secondaryLastName: formData?.secondaryLastName || "",
      secondaryEmail: formData?.secondaryEmail || "",
      secondaryTelephone: formData?.secondaryTelephone || "",
      showSecondaryContact: formData?.showSecondaryContact || false,
    },
  });
 
  // Watch all form values for changes
  const watchedValues = watch();
 
  // Update parent component when form values change (with debounce to prevent excessive updates)
  useEffect(() => {
    // Don't update during initial load
    if (!isInitialized) {
      return;
    }
 
    const timeoutId = setTimeout(() => {
      if (onContactDataChange) {
        onContactDataChange(watchedValues);
      }
    }, 300); // 300ms debounce
 
    return () => clearTimeout(timeoutId);
  }, [watchedValues, onContactDataChange, isInitialized]);
 
  // Initialize form with saved data on mount
  useEffect(() => {
    if (formData && !isInitialized) {   
      setValue("firstName", formData.firstName || "", { shouldValidate: false });
      setValue("lastName", formData.lastName || "", { shouldValidate: false });
      setValue("email", formData.email || "", { shouldValidate: false });
      setValue("telephone", formData.telephone || "", { shouldValidate: false });
      setValue("secondaryFirstName", formData.secondaryFirstName || "", { shouldValidate: false });
      setValue("secondaryLastName", formData.secondaryLastName || "", { shouldValidate: false });
      setValue("secondaryEmail", formData.secondaryEmail || "", { shouldValidate: false });
      setValue("secondaryTelephone", formData.secondaryTelephone || "", { shouldValidate: false });
      setValue("showSecondaryContact", formData.showSecondaryContact || false, { shouldValidate: false });
     
      setShowSecondaryContact(formData.showSecondaryContact || false);
      setIsInitialized(true);
    }
  }, [formData, setValue, isInitialized]);
 
  useImperativeHandle(ref, () => ({
    async getValidatedData() {
      const visibleFields = [
      "firstName",
      "lastName",
      "email",
      "telephone",
      ...(showSecondaryContact
        ? [
            "secondaryFirstName",
            "secondaryLastName",
            "secondaryEmail",
            "secondaryTelephone",
          ]
        : [])
    ];
      const valid = await trigger(visibleFields);
      return { isValid: valid, data: getValues() };
    },
  }));
 
  const handleToggleSecondary = (visible) => {
    setShowSecondaryContact(visible);
    setValue("showSecondaryContact", visible, { shouldValidate: true });
 
    if (!visible) {
      // Clear secondary contact fields
      resetField("secondaryFirstName", "");
      resetField("secondaryLastName", "");
      resetField("secondaryEmail", "");
      resetField("secondaryTelephone", "");
    }
  };
 
  return (
    <div className="field">
      <label className="label has-text-weight-semibold mb-4 contact-info-title">
        Contact information
      </label>
      <p className="help mb-4 contact-info-description">
        This is the person who will be contacted to arrange this moderation
        visit.
      </p>
 
      {/* Primary Fields */}
      <div className="card contact-card p-4 mb-5">
        <div className="columns">
          <div className="column">
            <div className="field">
              <label htmlFor="firstName" className="label field-label">
                First name <span className="has-text-danger">*</span>
              </label>
 
              <div className="control has-icons-right">
                <input
                  id="firstName"
                  required
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                  className={`input
                    ${errors.firstName ? "has-error" : ""}
                    ${dirtyFields.firstName && !errors.firstName ? "has-success" : ""}
                  `}
                  type="text"
                  {...register("firstName")}
                />
 
                <StatusIcon
                  error={errors.firstName}
                  dirtyField={dirtyFields.firstName}
                />
              </div>
 
              {errors.firstName && (
                <p id="firstName-error" className="help error-text">{errors.firstName.message}</p>
              )}
            </div>
          </div>
 
          <div className="column">
            <div className="field">
              <label htmlFor="lastName" className="label field-label">
                Last name <span className="has-text-danger">*</span>
              </label>
              <div className="control has-icons-right">
                <input
                  id="lastName"
                  required
                  aria-invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  className={`input
                    ${errors.lastName ? "has-error" : ""}
                    ${dirtyFields.lastName && !errors.lastName ? "has-success" : ""}
                  `}
                  type="text"
                  {...register("lastName")}
                />
 
                <StatusIcon
                  error={errors.lastName}
                  dirtyField={dirtyFields.lastName}
                />
              </div>
 
              {errors.lastName && (
                <p id="lastName-error" className="help error-text">{errors.lastName.message}</p>
              )}
            </div>
          </div>
        </div>
 
        <div className="field">
          <label htmlFor="email" className="label field-label">
            Email (your work email address){" "}
            <span className="has-text-danger">*</span>
          </label>
          <div className="control has-icons-right">
            <input
              id="email"
              required
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              className={`input
                ${errors.email ? "has-error" : ""}
                ${dirtyFields.email && !errors.email ? "has-success" : ""}
              `}
              type="email"
              {...register("email")}
            />
            <StatusIcon error={errors.email} dirtyField={dirtyFields.email} />
          </div>
          {errors.email && (
            <p id="email-error" className="help error-text">{errors.email.message}</p>
          )}
        </div>
 
        <div className="field mb-5">
          <label htmlFor="telephone" className="label field-label">
            Telephone <span className="has-text-danger">*</span>
          </label>
          <div className="control has-icons-right">
            <input
              id="telephone"
              required
              aria-invalid={!!errors.telephone}
              aria-describedby={errors.telephone ? "telephone-error" : undefined}
              className={`input
                ${errors.telephone ? "has-error" : ""}
                ${dirtyFields.telephone && !errors.telephone ? "has-success" : ""}
              `}
              type="tel"
              {...register("telephone")}
            />
            <StatusIcon
              error={errors.telephone}
              dirtyField={dirtyFields.telephone}
            />
          </div>
          {errors.telephone && (
            <p id="telephone-error" className="help error-text">{errors.telephone.message}</p>
          )}
        </div>
      </div>
 
      {/* Secondary Contact */}
      {showSecondaryContact && (
        <>
          <label className="label contact-info-title mb-4">
            Secondary contact
          </label>
          <div className="card contact-card p-4 mb-5">
            <div className="columns">
              <div className="column">
                <div className="field">
                  <label htmlFor="secondaryFirstName" className="label field-label">
                    First name <span className="has-text-danger">*</span>
                  </label>
                  <div className="control has-icons-right">
                    <input
                      id="secondaryFirstName"
                      required
                      aria-invalid={!!errors.secondaryFirstName}
                      aria-describedby={
                        errors.secondaryFirstName ? "secondaryFirstName-error" : undefined
                      }
                      className={`input
                        ${errors.secondaryFirstName ? "has-error" : ""}
                        ${dirtyFields.secondaryFirstName && !errors.secondaryFirstName ? "has-success" : ""}
                      `}
                      type="text"
                      {...register("secondaryFirstName")}
                    />
                    <StatusIcon
                      error={errors.secondaryFirstName}
                      dirtyField={dirtyFields.secondaryFirstName}
                    />
                  </div>
                  {errors.secondaryFirstName && (
                    <p id="secondaryFirstName-error" className="help error-text">
                      {errors.secondaryFirstName.message}
                    </p>
                  )}
                </div>
              </div>
 
              <div className="column">
                <div className="field">
                  <label htmlFor="secondaryLastName" className="label field-label">
                    Last name <span className="has-text-danger">*</span>
                  </label>
                  <div className="control has-icons-right">
                    <input
                      id="secondaryLastName"
                      required
                      aria-invalid={!!errors.secondaryLastName}
                        aria-describedby={
                        errors.secondaryLastName ? "secondaryLastName-error" : undefined
                      }
                      className={`input
                        ${errors.secondaryLastName ? "has-error" : ""}
                        ${dirtyFields.secondaryLastName && !errors.secondaryLastName ? "has-success" : ""}
                      `}
                      type="text"
                      {...register("secondaryLastName")}
                    />
                    <StatusIcon
                      error={errors.secondaryLastName}
                      dirtyField={dirtyFields.secondaryLastName}
                    />
                  </div>
                  {errors.secondaryLastName && (
                    <p id="secondaryLastName-error" className="help error-text">
                      {errors.secondaryLastName.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
 
            <div className="field">
              <label className="label field-label" htmlFor="secondaryEmail">
                Email (work email address){" "} <span className="has-text-danger">*</span>
              </label>
              <div className="control has-icons-right">
                <input
                  id="secondaryEmail"
                  required
                  aria-invalid={!!errors.secondaryEmail}
                  aria-describedby={errors.secondaryEmail ? "secondaryEmail-error" : undefined}
                  className={`input
                    ${errors.secondaryEmail ? "has-error" : ""}
                    ${dirtyFields.secondaryEmail && !errors.secondaryEmail ? "has-success" : ""}
                  `}
                  type="email"
                  {...register("secondaryEmail")}
                />
                <StatusIcon
                  error={errors.secondaryEmail}
                  dirtyField={dirtyFields.secondaryEmail}
                />
              </div>
              {errors.secondaryEmail && (
                <p id="secondaryEmail-error" className="help error-text">{errors.secondaryEmail.message}</p>
              )}
            </div>
 
            <div className="field">
              <label className="label field-label" htmlFor="secondaryTelephone">
                Telephone <span className="has-text-danger">*</span>
              </label>
              <div className="control has-icons-right">
                <input
                  id="secondaryTelephone"
                  required
                  aria-invalid={!!errors.secondaryTelephone}
                  aria-describedby={errors.secondaryTelephone ? "secondaryTelephone-error" : undefined}
                  className={`input
                    ${errors.secondaryTelephone ? "has-error" : ""}
                    ${dirtyFields.secondaryTelephone && !errors.secondaryTelephone ? "has-success" : ""}
                  `}
                  type="tel"
                  {...register("secondaryTelephone")}
                />
                <StatusIcon
                  error={errors.secondaryTelephone}
                  dirtyField={dirtyFields.secondaryTelephone}
                />
              </div>
              {errors.secondaryTelephone && (
                <p id="secondaryTelephone-error" className="help error-text">
                  {errors.secondaryTelephone.message}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="button is-small"
            onClick={() => handleToggleSecondary(false)}
          >
            Remove secondary contact
          </button>
        </>
      )}
 
      {!showSecondaryContact && (
        <div className="field">
          <button
            type="button"
            className="button is-small"
            onClick={() => handleToggleSecondary(true)}
          >
            Add secondary contact
          </button>
        </div>
      )}
 
      {/* Hidden field for schema condition */}
      <input type="hidden" {...register("showSecondaryContact")} />
    </div>
  );
});
 
export default ContactInformation;
 